import UString from "./UString.mts";
import UType from "./UType.mts";

namespace Case {
  export type Type = (typeof Case.types)[number];
  export namespace Type {
    export type Extra = (typeof Case.types.extra)[number];
    export type Extended = (typeof Case.types.extended)[number];
  }

  type NextUppercaseWord<S extends string> =
    S extends `${infer C0}${infer C1}${infer R}`
      ? `${C0}${C1}` extends Uppercase<`${C0}${C1}`>
        ? `${C0}${NextUppercaseWord<`${C1}${R}`>}`
        : ""
      : S extends `${infer C}${string}`
        ? C extends Uppercase<C>
          ? C
          : ""
        : "";

  type NextLowercaseGroup<S extends string> = S extends `${infer C0}${infer R}`
    ? C0 extends Lowercase<C0>
      ? `${C0}${NextLowercaseGroup<R>}`
      : ""
    : "";

  export type FromCamel<S extends string> = string extends S
    ? [string]
    : NextUppercaseWord<S> extends infer UppercaseWord extends string
      ? UppercaseWord extends ""
        ? S extends `${infer C0}${infer R}`
          ? NextLowercaseGroup<R> extends infer LowercaseGroup extends string
            ? [
                `${Lowercase<C0>}${LowercaseGroup}`,
                ...FromCamel<UString.AfterPrefix<R, LowercaseGroup>>,
              ]
            : never
          : []
        : [
            Lowercase<UppercaseWord>,
            ...FromCamel<UString.AfterPrefix<S, UppercaseWord>>,
          ]
      : never;

  export type FromPascal<S extends string> = FromCamel<S>;

  type NextSeparatorWord<
    S extends string,
    Separator extends string,
  > = S extends `${infer C0}${infer R}`
    ? C0 extends Separator
      ? ""
      : `${C0}${NextSeparatorWord<R, Separator>}`
    : "";

  type FromSeparated<
    S extends string,
    Separator extends string,
  > = string extends S
    ? [string]
    : NextSeparatorWord<S, Separator> extends infer Word extends string
      ? S extends `${Word}${infer R}`
        ? [
            Word,
            ...(R extends ""
              ? []
              : FromSeparated<UString.AfterPrefix<R, Separator>, Separator>),
          ]
        : never
      : never;

  // string cannot extend Lowercase<string> which breaks fallback type. We only
  // want to lowercase literals.
  type LowercaseLiteral<S extends string> = string extends S
    ? string
    : Lowercase<S>;

  export type FromKebab<S extends string> = S extends S
    ? FromSeparated<S, "-">
    : never;
  export type FromSnake<S extends string> = S extends S
    ? FromSeparated<S, "_">
    : never;
  export type FromSpaced<S extends string> = S extends S
    ? FromSeparated<S, " ">
    : never;
  export type FromConstant<S extends string> = S extends S
    ? FromSnake<LowercaseLiteral<S>>
    : never;
  export type FromSentence<S extends string> = S extends S
    ? FromSeparated<LowercaseLiteral<S>, " ">
    : never;
  export type FromTitle<S extends string> = S extends S
    ? FromSeparated<LowercaseLiteral<S>, " ">
    : never;

  export type ToPascal<Words extends string[]> = Words extends [
    infer Word extends string,
    ...infer Rest extends string[],
  ]
    ? `${UString.Capitalize<Word>}${ToPascal<Rest>}`
    : "";
  export type ToCamel<Words extends string[]> = UString.Decapitalize<
    ToPascal<Words>
  >;
  type ToSeparated<
    Words extends string[],
    Separator extends string,
  > = Words extends [infer Word extends string, ...infer Rest extends string[]]
    ? `${Word}${ToSeparated<Rest, Separator> extends infer Separated extends
        string
        ? Separated extends ""
          ? ""
          : `${Separator}${Separated}`
        : never}`
    : "";
  export type ToKebab<Words extends string[]> = ToSeparated<Words, "-">;
  export type ToSnake<Words extends string[]> = ToSeparated<Words, "_">;
  export type ToConstant<Words extends string[]> = Uppercase<ToSnake<Words>>;
  export type ToSpaced<Words extends string[]> = ToSeparated<Words, " ">;
  export type ToSentence<Words extends string[]> = UString.Capitalize<
    ToSeparated<Words, " ">
  >;
  export type ToTitle<Words extends string[]> = ToSeparated<
    CapitalizeEach<Words>,
    " "
  >;

  type CapitalizeEach<Words extends string[]> = Words extends [
    infer Word extends string,
    ...infer Rest extends string[],
  ]
    ? [UString.Capitalize<Word>, ...CapitalizeEach<Rest>]
    : [];
}

const splitCamelRegex = /[A-Z0-9]+[a-z]?/g;

class Case<const Words extends string[]> {
  static types = (() => {
    const types = ["camel", "kebab", "snake", "spaced"] as const;
    const extra = ["constant", "sentence", "title", "pascal"] as const;
    const extended = [...types, ...extra] as const;
    return Object.assign(types, { extra, extended });
  })();

  static fromCamel<S extends string>(string: S) {
    return this.init<UType.Narrow<Case.FromCamel<S>, string[]>>(
      "camel",
      string
    );
  }
  static fromPascal<S extends string>(string: S) {
    return this.fromCamel(string);
  }
  static fromKebab<S extends string>(string: S) {
    return this.init<Case.FromKebab<S>>("kebab", string);
  }
  static fromSnake<S extends string>(string: S) {
    return this.init<Case.FromSnake<S>>("snake", string);
  }
  static fromSpaced<S extends string>(string: S) {
    return this.init<Case.FromSpaced<S>>("spaced", string);
  }
  static fromConstant<S extends string>(string: S) {
    return this.init<Case.FromConstant<S>>("snake", string.toLowerCase());
  }
  static fromSentence<S extends string>(string: S) {
    return this.init<Case.FromSentence<S>>("spaced", string);
  }
  static fromTitle<S extends string>(string: S) {
    return this.init<Case.FromTitle<S>>("spaced", string);
  }
  static from<Type extends Case.Type.Extended, S extends string>(
    type: Type,
    string: S
  ) {
    return this[`from${UString.capitalize(type)}`](string);
  }

  private static init<Words extends string[]>(
    sourceCase: Case.Type,
    string: string
  ) {
    return new Case<Words>(Case.splitByType[sourceCase](string) as Words);
  }

  // constructor(
  //   private sourceCase: Case.Type,
  //   private string: string
  // ) {}

  constructor(public readonly words: Words) {}

  private static splitByType: Record<Case.Type, (string: string) => string[]> =
    {
      camel: (string: string) =>
        string.length === 1
          ? [string.toLowerCase()]
          : string
              .replace(splitCamelRegex, (string, offset) =>
                `${offset > 0 ? "-" : ""}${
                  string.length <= 2
                    ? string
                    : // Uppercase word
                      `${string.slice(0, -2)}${
                        /[a-z]/.test(string.at(-1) || "") ? "-" : ""
                      }${string.slice(-2)}`
                }`.toLowerCase()
              )
              .split("-"),
      kebab: (string: string) => string.split("-"),
      snake: (string: string) => string.split("_"),
      spaced: (string: string) => string.split(" "),
    };

  toPascal() {
    return `${this.words.map(UString.capitalize).join("")}` as Case.ToPascal<Words>;
  }
  toCamel() {
    return UString.decapitalize(this.toPascal()) as Case.ToCamel<Words>;
  }
  toKebab() {
    return this.words.join("-") as Case.ToKebab<Words>;
  }
  toSnake() {
    return this.words.join("_") as Case.ToSnake<Words>;
  }
  toConstant() {
    return this.toSnake().toUpperCase() as Case.ToConstant<Words>;
  }
  toSpaced() {
    return this.words.join(" ") as Case.ToSpaced<Words>;
  }
  toSentence() {
    return UString.capitalize(this.toSpaced()) as Case.ToSentence<Words>;
  }
  toTitle() {
    return this.words.map(UString.capitalize).join(" ") as Case.ToTitle<Words>;
  }

  // Inference through `this[`to${Capitalize<Type>}`]()` causes stack overflow
  // during type check, must explicitly map return type.
  to<Type extends Case.Type.Extended>(
    type: Type
  ): {
    camel: Case.ToCamel<Words>;
    kebab: Case.ToKebab<Words>;
    snake: Case.ToSnake<Words>;
    spaced: Case.ToSpaced<Words>;
    constant: Case.ToConstant<Words>;
    sentence: Case.ToSentence<Words>;
    title: Case.ToTitle<Words>;
    pascal: Case.ToPascal<Words>;
  }[Type] {
    return (this as any)[`to${UString.capitalize(type)}`]();
  }
}

export default Case;
